/**
 * DashboardLayout — root chrome for every authenticated route.
 *
 * Renders the persistent sidebar + topbar and `<Outlet>`s the active route.
 *
 * Sidebar composition (in order, conditional on user role):
 *   1. `getBaseNavigation(...)` — 13-module ERP nav; self-service items (My
 *      Timesheet, Clock In, Time Off Requests, My Profile) are children of their
 *      parent modules (T&A, H&O) and gated by RBAC permission keys.
 *   2. `studentPortalNavigation` / `parentPortalNavigation` — for the SIS
 *      student & parent surfaces.
 *   4. `districtAdminNavigation` + `districtToolsNavigation` — for tenant /
 *      IT admins (Identity Setup Wizard, SAML, LDAP, SFTP, Pilot Discovery).
 *   5. `adminNavigation` + `platformToolsNavigation` + `districtManagement-
 *      Navigation` — for k12erp super admins (with tenant selector).
 *
 * Permission filtering:
 *   - Planned pages are declared once in `plannedModules.js`. The sidebar and
 *     the router both read that list. Built modules stay in this file because
 *     their children are permission-gated.
 *   - A nav item is shown when its module is enabled for the tenant
 *     (`enabled_modules` from `/api/rbac/me/`). Super admins see every module.
 *   - Add-on features hide individual children via `featureKey` lookups in
 *     `module_features`.
 *
 * Other moving parts:
 *   - `<NotificationBell>` fetches unread notifications from `GET /api/notifications/`.
 *   - Impersonation banner appears when `useAuth().isImpersonating` is true.
 *   - Mobile drawer mirrors the desktop sidebar; collapse state persists in
 *     component-level state (no localStorage to keep behavior predictable
 *     across multi-tab walkthroughs).
 *
 * Touch carefully — every authenticated screen depends on this layout.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { cn } from '../lib/utils';
import useDueBannerItems from '../hooks/useDueBannerItems';
import SubmissionReminderDialog from '../components/attendance/SubmissionReminderDialog';
import NotificationHistoryModal from '../components/notifications/NotificationHistoryModal';
import UpcomingDueBanner from '../components/UpcomingDueBanner';
import OvertimeRequestBanner from '../components/OvertimeRequestBanner';
import { canViewSubmodule, hasPermission } from '../lib/rbac';
import { ROSTER_IMPORT_FLAG, rosterImportNavVisible } from '../lib/rosterImportAccess';
import { NFC_TAP_FLAG, nfcTagNavVisible } from '../lib/nfcTagAdminAccess';
import {
  isActive as sidebarIsActive,
  isParentActive as sidebarIsParentActive,
  findGroupToExpand,
} from './sidebarMatching';
import { moduleKeysFromNav } from './sidebarRouteCheck';
import {
  DISTRICT_SETTINGS,
  ONBOARDING_WIZARD,
  parentPortalNavigation,
  plannedStaffNavItems,
  studentPortalNavigation,
  withPlannedSection,
} from './plannedModules';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  LayoutDashboard,
  DollarSign,
  Landmark,
  Clock, 
  GraduationCap, 
  Settings,
  ChevronLeft,
  ChevronRight,
  Bell,
  Search,
  LogOut,
  Building2,
  FileText,
  UserCog,
  Menu,
  UserPlus,
  Shield,
  ShieldCheck,
  UserSearch,
  ClipboardCheck,
  Zap,
  MapPin,
  Eye,
  Rocket,
  Bug,
  Plug,
  Database,
  Brain,
  BookOpen,
  Upload,
  Video,
  Sparkles,
  ToggleLeft,
  Database as DbIcon,
  Network,
  X
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

export { parentPortalNavigation, studentPortalNavigation };

// Student and parent accounts get their own sidebar. Module visibility for
// everyone else comes from entitlements and permission keys.
const STUDENT_ROLES = ['STUDENT'];
const PARENT_ROLES = ['PARENT'];
const SUPERVISOR_ROLES = ['SUPERVISOR'];

// SD308 district-admin accounts that get a STRIPPED sidebar (UI-only). They're
// still TENANT_ADMIN at the API layer — this is purely a frontend whitelist
// to keep the demo sidebar focused on the modules SD308 actually pilots.
// Whitelist (anything not on these lists is hidden): top-level = Hiring &
// Onboarding, Time & Attendance, Training Center; District Setup section =
// Roles & Access Matrix, Integrations Hub, Pilot Discovery, SFTP Time Clock.
const SD308_ADMIN_EMAILS = [
  'admin@sd308.org',
  'bkiger.admin@sd308.org',
  'kmcdonald.admin@sd308.org',
  'asitar.admin@sd308.org',
];
const SD308_ADMIN_NAV_WHITELIST = new Set([
  'Dashboard',
  'Hiring & Onboarding',
  'Time & Attendance',
  'Grants Management',
  'Training Center',
]);
const SD308_ADMIN_DISTRICT_SECTION_WHITELIST = new Set([
  'Roles & Access Matrix',
  'Role Hierarchy',
  'District Setup',
]);

// Base navigation items with required module permissions.
// `isDemoImpersonating` skips the inline hiddenForRoles / visibleForRoles
// filters so the SD308 demo's Access Matrix becomes the sole source of truth
// for sub-item visibility (canAccessRoute gates them at render time). Without
// this bypass, toggling D→A on an item like "Background Checks" for
// IT_DIRECTOR couldn't make it appear — the static filter would strip it
// before any matrix check ran.
// `userPermissions` gates sub-items by the RBAC registry's submodule .view keys
// (e.g. 'hiring.applicant_tracking_system.view'). Defensive: an item is only
// HIDDEN when permissions are loaded AND the key is absent — so the nav never
// blanks out before /rbac/me/ resolves or during the backend rollout.
export const getBaseNavigation = (isSupervisor, userRoles = [], isDemoImpersonating = false, userPermissions = [], { rosterImport = false, nfcTags = false } = {}) => [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, alwaysShow: true },
  // TODO(concierge): AI Concierge hidden for all roles until integrations land / we have richer data to feed the LLM. Re-enable nav item + route (App.js) + tour (walkthroughData/TrainingCenter).
  // { name: 'AI Concierge', href: '/concierge', icon: Brain, alwaysShow: true },
  {
    name: 'Hiring & Onboarding',
    href: '/hiring',
    icon: UserPlus,
    module: 'hiring',
    children: (() => {
      const permFilter = (item) => isDemoImpersonating || !item.permission || !userPermissions.length || hasPermission(item.permission, { permissions: userPermissions });

      const recruiting = [
        { name: 'Job Requisitions', href: '/hiring/requisitions', permission: 'hiring.applicant_tracking_system.view' },
        { name: 'Applicants', href: '/hiring/applicants', permission: 'hiring.applicant_tracking_system.view' },
        { name: 'Interviews', href: '/hiring/interviews', permission: 'hiring.applicant_tracking_system.view' },
        { name: 'Offer Letters', href: '/hiring/offers', permission: 'hiring.offers_e_signature.view' },
        { name: 'Background Checks', href: '/hiring/background-checks', permission: 'hiring.background_checks.view' },
      ].filter(permFilter);

      const onboardingCat = [
        { name: 'New Hire Checklist', href: '/hiring/checklist', permission: 'hiring.hire_onboarding.view' },
        { name: 'Onboarding', href: '/onboarding', permission: 'hiring.hire_onboarding.view' },
      ].filter(permFilter);

      const unionContracts = [
        { name: 'Union Contract Rules', href: '/hiring/union-rules', permission: 'hiring.union_contract_rules.view', featureKey: 'hiring.union_rules' },
        { name: 'Union Contract Configuration', href: '/hiring/cba-rules', permission: 'hiring.union_contract_rules.edit', featureKey: 'hiring.union_rules' },
      ].filter(permFilter);

      const employeesExport = [
        { name: 'All Employees', href: '/hr/employees', permission: 'hiring.employee.view' },
        // Live screen is ContractTenure over hr EmployeeContract / ContractTemplate.
        // Payroll is not an entitled module, so the /payroll/contracts nav target
        // is surfaced here (HR-adjacent pages live under Hiring & Onboarding).
        // Permission matches EmployeeContractViewSet (hiring.hire_onboarding).
        { name: 'Employee Contracts', href: '/payroll/contracts', permission: 'hiring.hire_onboarding.view' },
        { name: 'Export to HR Systems', href: '/hiring/export', permission: 'hiring.export_to_hr.view' },
      ].filter(permFilter);

      const category = (name, children) => (children.length > 0 ? [{ name, isCategory: true, children }] : []);

      const items = [
        { name: 'Dashboard', href: '/hiring', permission: 'hiring.dashboard.view' },
        ...category('Recruiting', recruiting),
        ...category('Onboarding', onboardingCat),
        ...category('Union Contracts', unionContracts),
        ...category('Employees & Export', employeesExport),
      ];
      return items
        .filter(item => isDemoImpersonating || !item.hiddenForRoles?.some(r => userRoles.includes(r)))
        .filter(item => {
          if (isDemoImpersonating || !userPermissions.length) return true;
          if (item.isCategory) return true;
          if (item.permission) return hasPermission(item.permission, { permissions: userPermissions });
          return true;
        });
    })(),
  },
  // Human Resources nav section intentionally omitted from the main nav.
  // HR is an API/permission namespace, not a product module shown to district users.
  // HR-adjacent views (employee list, profile, etc.) surface as children under H&O above.
  {
    name: 'Time & Attendance',
    href: '/attendance',
    icon: Clock,
    module: 'attendance',
    children: (() => {
      const permFilter = (item) => isDemoImpersonating || !item.permission || !userPermissions.length || hasPermission(item.permission, { permissions: userPermissions });

      // Collapsible categories — each filtered here so empty groups are dropped.
      const absenceMgmt = [
        { name: 'Requests', href: '/attendance/requests', permission: 'attendance.authorization_requests.view' },
        { name: 'FMLA', href: '/attendance/fmla', permission: 'attendance.fmla.view' },
        { name: 'Absence Coverage', href: '/attendance/substitute-calling', permission: 'attendance.substitute_management.view' },
        { name: 'Leave Balances (Payroll)', href: '/admin/payroll-leave-balances', permission: 'attendance.absence_types.view' },
        { name: 'Leave Balances (Roster)', href: '/admin/leave-balances', permission: 'attendance.absence_types.view' },
      ].filter(permFilter);

      // District Calendar is the admin/edit-capable view — gated on the
      // unified hiring.calendar.edit permission (see PR #422). The read-only
      // self-service My Calendar lives in the My Pages category below.
      const schedulingCal = [
        { name: 'Workforce Scheduling', href: '/attendance/scheduling', permission: 'attendance.scheduling.view' },
        { name: 'District Calendar', href: '/hiring/calendar', permission: 'hiring.calendar.edit' },
      ].filter(permFilter);
      // Hidden unless the district import flag is on. The caller already
      // required scheduling edit, or a super admin. Flag off leaves this
      // menu exactly as it was.
      if (rosterImport) {
        schedulingCal.push({
          name: 'Roster import',
          href: '/attendance/roster-import',
          permission: 'attendance.scheduling.edit',
        });
      }

      const reportsAnalytics = [
        { name: 'Reports & Analytics', href: '/attendance/reporting', permission: 'attendance.reports_analytics.view' },
        { name: 'Cost-Per-Absence', href: '/attendance/cost-analysis', permission: 'attendance.reports_analytics.view', featureKey: 'attendance.cost_analysis' },
        { name: 'Absence Forecasting (AI)', href: '/attendance/absence-forecast', permission: 'attendance.ai_tools.view', featureKey: 'attendance.ai_forecast' },
        { name: 'Smart Savings (AI)', href: '/attendance/smart-savings', permission: 'attendance.ai_tools.view', featureKey: 'attendance.smart_savings' },
      ].filter(permFilter);

      const compliance = [
        { name: 'State Reports (ISBE)', href: '/attendance/state-reports', permission: 'attendance.compliance_isbe.view', featureKey: 'attendance.isbe' },
        { name: 'BIPA Compliance', href: '/attendance/bipa', permission: 'attendance.compliance_isbe.view', featureKey: 'attendance.bipa' },
      ].filter(permFilter);

      const configRules = [
        { name: 'Rule Set Assignment', href: '/admin/attendance-rule-sets', permission: 'attendance.rule_set_assignment.view' },
        { name: 'Absence Types', href: '/admin/absence-types', permission: 'attendance.absence_types.view' },
        { name: 'Pay Rules', href: '/attendance/pay-rules', permission: 'attendance.pay_rules.view' },
        { name: 'Badge scan settings', href: '/attendance/badge-scan-settings', permission: 'attendance.scheduling.edit' },
        { name: 'Scan anomaly review', href: '/attendance/scan-anomalies', permission: 'attendance.timesheets_exceptions.view' },
        { name: 'Clock-in retention', href: '/attendance/clock-in-retention', permission: 'attendance.clock_records.retention_configure' },
        { name: 'Clock-in disposal', href: '/attendance/clock-in-disposal', permission: 'attendance.clock_records.disposal_approve' },
        { name: 'Punch Photo Retention', href: '/attendance/punch-photo-retention', permission: 'attendance.punch_photos.retention_configure' },
        { name: 'Export delta rules', href: '/attendance/export-delta-rules', permission: 'attendance.export_delta_rules.view' },
        { name: 'Timesheet Reminders', href: '/admin/timesheet-reminder-rules', permission: 'attendance.timesheet_reminders.view' },
        { name: 'Geofence Zones', href: '/attendance/geofencing', permission: 'attendance.geofence_zones.view' },
        { name: 'IP Allowlist', href: '/attendance/ip-allowlist', permission: 'attendance.ip_allowlist.view' },
        { name: 'Device Integrations', href: '/attendance/integrations', permission: 'attendance.device_integrations.view' },
        ...(nfcTags ? [{ name: 'NFC tags', href: '/attendance/nfc-tags', permission: 'attendance.nfc_tags.view' }] : []),
      ].filter(permFilter);

      // Self-service pages grouped under a collapsible "My Pages" category at the bottom.
      const myPagesChildren = [
        { name: 'My Coverage Offers', href: '/attendance/my-coverage-offers', permission: 'attendance.my_timesheet.view' },
        { name: 'My Timesheet', href: '/attendance/my-timesheet', permission: 'attendance.my_timesheet.view' },
        { name: 'My Requests', href: '/attendance/my-time-off', permission: 'attendance.leave.view' },
        { name: 'My Profile', href: '/hr/my-profile', permission: 'attendance.my_profile.view' },
        { name: 'My Documents', href: '/hr/my-documents', permission: 'attendance.my_documents.view' },
        // Read-only self-service calendar; District Calendar (edit-capable)
        // lives in the Scheduling & Calendar category above.
        { name: 'My Calendar', href: '/hiring/my-calendar', permission: 'hiring.calendar.view' },
      ].filter(permFilter);

      // Collapsible category — expansion state is persisted in localStorage
      // (see toggleCategory / expandedCategories above), so a user's open/closed
      // choices survive page reloads. Skipped entirely if no children are visible.
      const category = (name, children) => (children.length > 0 ? [{ name, isCategory: true, children }] : []);

      const items = [
        { name: 'Dashboard', href: '/attendance', permission: 'attendance.supervisor_inbox.view' },
        // Employee Timesheets hub — tabs for Employee Timesheet, Supervisor Inbox, Exceptions Log.
        // permissionAny: visible if the user holds any of the underlying tab permissions;
        // the hub page itself filters which tabs render.
        {
          name: 'Employee Timesheets',
          href: '/attendance/employee-timesheets',
          permissionAny: ['attendance.timesheets_exceptions.view', 'attendance.supervisor_inbox.view'],
        },
        // Payroll Export sits beside Employee Timesheets because it reads
        // the same approved-timesheet data and is Payroll's downstream review
        // surface (PB-16..19). The in-page Approvals tab (search + nudge)
        // lives inside this screen, not as a separate nav entry.
        {
          name: 'Payroll Export',
          href: '/attendance/payroll-export',
          permission: 'attendance.payroll_export.view',
        },
        // Grant Approvals stays flat at its original position — gated on
        // GrantApprover roster membership, not a permission key (see
        // isGrantApprover from /rbac/me/ and childFilter above).
        { name: 'Grant Approvals', href: '/attendance/grant-approvals', requiresGrantApprover: true },
        ...category('Absence Management', absenceMgmt),
        ...category('Scheduling & Calendar', schedulingCal),
        ...category('Reports & Analytics', reportsAnalytics),
        ...category('Compliance', compliance),
        ...category('Configuration & Rules', configRules),
        { name: 'Clock In', href: '/attendance/mobile-clock', permission: 'attendance.my_timesheet.view' },
        ...category('My Pages (Self Service)', myPagesChildren),
      ];
      return items
        .filter(item => isDemoImpersonating || !item.hiddenForRoles?.some(r => userRoles.includes(r)))
        .filter(item => isDemoImpersonating || !item.visibleForRoles || item.visibleForRoles.some(r => userRoles.includes(r)))
        .filter(item => {
          if (isDemoImpersonating || !userPermissions.length) return true;
          if (item.isCategory) return true;
          if (item.permission) return hasPermission(item.permission, { permissions: userPermissions });
          if (item.permissionAny) return item.permissionAny.some(p => hasPermission(p, { permissions: userPermissions }));
          return true;
        });
    })(),
  },
  {
    name: 'Grants Management',
    href: '/grants',
    icon: Landmark,
    module: 'grants',
    children: (() => {
      const permFilter = (item) => isDemoImpersonating || !item.permission || !userPermissions.length || hasPermission(item.permission, { permissions: userPermissions });

      const preAward = [
        { name: 'Search', href: '/grants/search', permission: 'grants.search.view' },
        { name: 'AI Grant Writer', href: '/grants/ai-writer', permission: 'grants.ai_writer.view' },
        { name: 'Applications', href: '/grants/applications', permission: 'grants.applications.view' },
      ].filter(permFilter);

      const awardedGrants = [
        { name: 'Awarded Grants Configuration', href: '/grants/awarded-config', permission: 'grants.awarded_grants_config.view' },
        { name: 'Budgets', href: '/grants/budgets', permission: 'grants.budgets.view' },
        { name: 'Deliverables', href: '/grants/deliverables', permission: 'grants.deliverables.view' },
        { name: 'Calendar', href: '/grants/calendar', permission: 'grants.calendar.view' },
        { name: 'Checklists', href: '/grants/checklists', permission: 'grants.checklists.view' },
      ].filter(permFilter);

      // Progress Reports reads live TimeClock/GrantType data, not the
      // grants.Grant GMS models used by the rest of this module — the
      // permission gate reflects that.
      const financeReporting = [
        { name: 'Drawdown', href: '/grants/drawdown', permission: 'grants.drawdown.view' },
        { name: 'Effort Reporting', href: '/grants/effort', permission: 'grants.effort_reporting.view' },
        { name: 'Progress Reports', href: '/grants/reports', permission: 'attendance.grant_hours_overview.view' },
      ].filter(permFilter);

      const category = (name, children) => (children.length > 0 ? [{ name, isCategory: true, children }] : []);

      const items = [
        { name: 'Dashboard', href: '/grants', permission: 'grants.dashboard.view' },
        ...category('Pre-Award', preAward),
        ...category('Awarded Grants', awardedGrants),
        ...category('Finance & Reporting', financeReporting),
      ];
      return items
        .filter(item => isDemoImpersonating || !item.hiddenForRoles?.some(r => userRoles.includes(r)))
        .filter(item => {
          if (isDemoImpersonating || !userPermissions.length) return true;
          if (item.isCategory) return true;
          if (item.permission) return hasPermission(item.permission, { permissions: userPermissions });
          return true;
        });
    })(),
  },
  { name: 'Training Center', href: '/training', icon: GraduationCap, alwaysShow: true },
  // Unbuilt modules. Paths and labels come from plannedModules.js, which
  // App.js also mounts, and render together under the Planned heading.
  ...plannedStaffNavItems(),
];

// /rbac/me/ did not return flag state. A missing flag is off, so the
// super-admin fallback must not invent roster import or NFC tags.
export function navigationAfterPermissionFetchFailure() {
  return getBaseNavigation(false, [], false, [], { rosterImport: false, nfcTags: false });
}

// Grant review is an assignment-based route. A named reviewer must retain a
// path to their queue even when their ordinary roles do not enable the wider
// attendance module.
export const hasNavigationModuleAccess = (
  module,
  { enabledModules = [], isSuperAdmin = false, isGrantApprover = false } = {},
) => (
  isSuperAdmin
  || enabledModules.includes(module)
  || (module === 'attendance' && isGrantApprover)
);

// Platform Admin navigation - for k12erp super admins
export const adminNavigation = [
  { name: 'Platform Overview', href: '/admin', icon: Building2 },
  { name: 'Master Admin Dashboard', href: '/admin/master-admin/dashboard', icon: Building2 },
  { name: 'Tenant Management', href: '/admin/tenants', icon: Settings },
  { name: 'Billing & Invoices', href: '/admin/billing/invoices', icon: DollarSign },
  { name: 'User Management', href: '/admin/users', icon: UserCog },
  { name: 'RBAC', href: '/admin/rbac', icon: Shield },
  { name: 'Position Mapping', href: '/admin/position-mapping', icon: UserCog },
  { name: 'Integrations', href: '/admin/integrations', icon: Plug },
  { name: 'Infinite Visions ERP', href: '/settings/integrations/infinite-visions', icon: Plug },
  { name: 'Rules Engine', href: '/admin/rules', icon: Zap },
  { name: 'Feature Flags', href: '/admin/feature-flags', icon: ToggleLeft },
  { name: 'Direct Competition Hub', href: '/admin/direct-competition', icon: Sparkles },
  { name: 'Maintenance', href: '/admin/maintenance', icon: ClipboardCheck },
  { name: 'Identity & SSO', href: '/admin/identity-hub', icon: Shield },
  { name: 'School Policies', href: '/admin/policies', icon: ShieldCheck },
  { name: 'Documentation Hub', href: '/admin/docs', icon: BookOpen },
  { name: 'Group → Role Mapping', href: '/admin/group-role-mapping', icon: ShieldCheck },
  { name: 'User Lookup', href: '/admin/user-lookup', icon: UserSearch },
  { name: 'Import Center', href: '/imports', icon: Upload },
  { name: 'Seed Management', href: '/admin/seed-management', icon: Database },
];

// Platform Tools - Platform-level tools for k12erp super admins
export const platformToolsNavigation = [
  { name: 'Diagnostics', href: '/admin/diagnostics', icon: ClipboardCheck },
  { name: 'Config Versioning', href: '/admin/config', icon: FileText },
  { name: 'Bug Fixing', href: '/admin/bug-fixing', icon: Bug },
  { name: 'RFP Center', href: '/admin/rfp', icon: FileText },
  { name: 'Pricing Calculator', href: '/admin/pricing', icon: DollarSign },
  { name: 'Specialized Services', href: '/admin/specialized-marketplace', icon: Brain },
  { name: 'Teletherapy', href: '/admin/teletherapy', icon: Video },
  { name: 'University Partners', href: '/admin/university-partnerships', icon: GraduationCap },
];

// District Management - Platform admin tools that work per-district (with district selector)
export const districtManagementNavigation = [
  { name: ONBOARDING_WIZARD.name, href: ONBOARDING_WIZARD.href, icon: Rocket, dynamic: true, planned: true },
  { name: 'Simulation Engine', href: '/admin/simulation', icon: Eye },
  { name: 'District Health', href: '/admin/operations', icon: Zap },
];

// District Admin navigation. Every item is gated by a registry submodule key —
// no role checks, no bypasses. TENANT_ADMIN holds all keys; IT_ADMIN holds
// only the subset defined in rbac_roles.json.
export const districtAdminNavigation = [
  { name: ONBOARDING_WIZARD.name, href: ONBOARDING_WIZARD.href, icon: Rocket, dynamic: true, planned: true, submodule: 'admin.onboarding_wizard' },
  { name: 'Position Mapping', href: '/admin/position-mapping', icon: UserCog, submodule: 'admin.position_mapping' },
  { name: 'Roles & Access Matrix', href: '/admin/rbac', icon: Shield, submodule: 'admin.rbac' },
  { name: 'Role Hierarchy', href: '/admin/role-hierarchy', icon: Network, submodule: 'admin.role_hierarchy' },
  { name: 'District Setup', href: '/admin/district-setup', icon: Building2, submodule: 'admin.district_setup' },
  { name: 'Pay Profiles', href: '/admin/pay-profiles', icon: DollarSign, submodule: 'payroll.pay_profiles' },
  { name: 'Integrations Hub', href: '/admin/integrations', icon: Plug, submodule: 'admin.integrations_hub' },
  { name: 'Billing & Invoices', href: '/admin/billing/invoices', icon: DollarSign, submodule: 'admin.billing' },
  { name: 'Infinite Visions ERP', href: '/settings/integrations/infinite-visions', icon: Plug, submodule: 'admin.billing' },
  { name: 'Identity Setup Wizard', href: '/admin/identity/onboard', icon: Sparkles, submodule: 'admin.identity_sso' },
  { name: 'Pilot Discovery', href: '/admin/discovery', icon: ClipboardCheck, submodule: 'admin.pilot_discovery' },
  { name: 'SAML 2.0 SSO', href: '/admin/identity/saml', icon: Plug, submodule: 'admin.identity_sso' },
  { name: 'LDAP / AD Sync', href: '/admin/identity/ldap', icon: Plug, submodule: 'admin.identity_sso' },
  { name: 'SFTP Time Clock', href: '/admin/attendance/sftp', icon: Plug, submodule: 'admin.sftp_time_clock' },
  { name: DISTRICT_SETTINGS.name, href: DISTRICT_SETTINGS.href, icon: Settings, planned: true, submodule: 'admin.district_settings' },
  { name: 'Documentation Hub', href: '/admin/docs', icon: BookOpen, submodule: 'admin.district_settings' },
];

// District Tools - For district admins (Simulation + Operations only)
export const districtToolsNavigation = [
  { name: 'Simulation Engine', href: '/admin/simulation', icon: Eye },
  { name: 'District Health', href: '/admin/operations', icon: Zap },
];


// SessionStorage key for dismissed submission-reminder notification
// ids. Kept per-tab so a "Not now" click quiets the dialog for the
// rest of the session without also silencing a NEW reminder the
// backend may fire later.
const SUBMISSION_REMINDER_DISMISSED_KEY = 'k12erp.dashboard.submissionRemindersDismissed';

// Minimal Notification Bell Component
const NotificationBell = () => {
  const [open, setOpen] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  // Full-history modal, opened from the "See all" button in the dropdown
  // footer. The bell itself only ever shows unread notifications (client
  // filtered), so the modal is the only surface for anything already read
  // or older than the first page.
  const [historyOpen, setHistoryOpen] = React.useState(false);
  // Submission-reminder pop-up: opens the moment a "Submit your
  // timesheet" notification lands (via 60s poll below) and we haven't
  // already dismissed that specific id in this session.
  const [reminderNotif, setReminderNotif] = React.useState(null);
  const ref = React.useRef(null);
  const navigate = useNavigate();

  // Close on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchNotifications = React.useCallback(() => {
    api.get('/notifications/').then(res => {
      const data = res.data.results || res.data;
      const unread = data.filter(n => !n.is_read);
      setNotifications(unread);
      // Open the submission-reminder dialog for the first unread
      // "Submit your timesheet" notification we haven't already
      // dismissed this session. Matches by title because the
      // Notification model doesn't have a dedicated subtype field
      // and every submission reminder the backend writes uses this
      // exact title (apps/attendance/tasks.py :: send_submission_reminders).
      try {
        const dismissed = new Set(JSON.parse(sessionStorage.getItem(SUBMISSION_REMINDER_DISMISSED_KEY) || '[]'));
        const pending = unread.find((n) => n.title === 'Submit your timesheet' && !dismissed.has(n.id));
        if (pending) {
          setReminderNotif((prev) => (prev && prev.id === pending.id ? prev : pending));
        }
      } catch (_) { /* private browsing */ }
    }).catch(() => {});
  }, []);

  // Load on mount so badge is visible before opening
  React.useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // Refresh when dropdown opens
  React.useEffect(() => { if (open) fetchNotifications(); }, [open, fetchNotifications]);

  // Poll for new notifications while the app is just sitting open -- without
  // this, something like the shift-length warning could notify a supervisor
  // and they'd never see it unless they happened to reload or reopen the
  // bell. Same 60s cadence already used for the RBAC permissions poll below.
  React.useEffect(() => {
    const POLL_MS = 60_000;
    const id = setInterval(fetchNotifications, POLL_MS);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  const hasNotifications = notifications.length > 0;

  const clearAll = () => {
    api.post('/notifications/mark_all_read/').catch(() => {});
    setNotifications([]);
  };

  const handleNotificationClick = (n) => {
    api.post(`/notifications/${n.id}/mark_read/`).catch(() => {});
    setNotifications((prev) => prev.filter((x) => x.id !== n.id));
    setOpen(false);
    if (n.action_url) navigate(n.action_url);
  };

  // Persist the dismissed id so a follow-up poll (same session) doesn't
  // re-open the dialog for the same reminder. Cross-session behaviour
  // is intentionally different: sessionStorage clears on tab close, so
  // a fresh login gets fresh reminders.
  const rememberDismissed = (id) => {
    try {
      const raw = sessionStorage.getItem(SUBMISSION_REMINDER_DISMISSED_KEY);
      const set = new Set(raw ? JSON.parse(raw) : []);
      set.add(id);
      sessionStorage.setItem(SUBMISSION_REMINDER_DISMISSED_KEY, JSON.stringify([...set]));
    } catch (_) { /* private browsing */ }
  };

  const handleReminderDismiss = () => {
    if (!reminderNotif) return;
    const id = reminderNotif.id;
    rememberDismissed(id);
    api.post(`/notifications/${id}/mark_read/`).catch(() => {});
    setNotifications((prev) => prev.filter((x) => x.id !== id));
    setReminderNotif(null);
  };

  const handleReminderAction = () => {
    if (!reminderNotif) return;
    const id = reminderNotif.id;
    const action = reminderNotif.action_url || '/attendance/my-timesheet';
    rememberDismissed(id);
    api.post(`/notifications/${id}/mark_read/`).catch(() => {});
    setNotifications((prev) => prev.filter((x) => x.id !== id));
    setReminderNotif(null);
    navigate(action);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        className="relative p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        data-testid="notifications-btn"
        onClick={() => setOpen(!open)}
      >
        <Bell size={20} />
        {hasNotifications && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-80 bg-white border border-slate-200 rounded-lg shadow-xl z-50">
          <div className="p-3 border-b border-slate-100">
            <h3 className="font-semibold text-sm text-slate-900">Notifications</h3>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {hasNotifications ? (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className="p-3 border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                  onClick={() => handleNotificationClick(n)}
                  data-testid={`notification-item-${n.id}`}
                >
                  <p className="text-sm font-medium text-slate-800">{n.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                  <p className="text-xs text-slate-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : 'Just now'}</p>
                </div>
              ))
            ) : (
              <div className="p-6 text-center">
                <p className="text-sm italic text-gray-400">No notifications available</p>
              </div>
            )}
          </div>
          <div className="p-2 border-t border-slate-100 flex gap-2">
            <button data-testid="dashboardlayout-see-all-btn"
              className="flex-1 text-xs text-blue-600 hover:text-blue-700 py-1"
              onClick={() => { setHistoryOpen(true); setOpen(false); }}
            >
              See all
            </button>
            {hasNotifications && (
              <button data-testid="dashboardlayout-clear-all-btn"
                className="flex-1 text-xs text-blue-600 hover:text-blue-700 py-1"
                onClick={clearAll}
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      )}
      <NotificationHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onNotificationRead={(id) => setNotifications((prev) => prev.filter((x) => x.id !== id))}
        onHistoryCleared={() => setNotifications([])}
      />
      <SubmissionReminderDialog
        open={!!reminderNotif}
        message={reminderNotif?.message}
        onDismiss={handleReminderDismiss}
        onAction={handleReminderAction}
      />
    </div>
  );
};

function PlannedMark() {
  return (
    <span className="ml-auto shrink-0 rounded bg-amber-900/50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-200">
      Planned
    </span>
  );
}

const DashboardLayout = () => {
  const { user, logout, isSuperAdmin, isImpersonating, originalAdmin, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  // "Due soon" banner — computed once here so it shows on every
  // authenticated page (payroll deadlines, admin-configured Deadline
  // banners, personal reminders), not just the Dashboard.
  const dueBannerItems = useDueBannerItems();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const [expandedMenu, setExpandedMenu] = useState(null);
  // Which nav categories are open. Persisted so a user's per-category
  // collapse/expand choices survive page reloads (Aidan/Dimitri UX ask).
  const CATEGORY_STORAGE_KEY = 'nav.expandedCategories.v1';
  const [expandedCategories, setExpandedCategories] = useState(() => {
    try {
      const raw = window.localStorage.getItem(CATEGORY_STORAGE_KEY);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  });
  const toggleCategory = (name) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      try { window.localStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(Array.from(next))); } catch { /* ignore quota errors */ }
      return next;
    });
  };
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [userScope, setUserScope] = useState(null);
  const [enabledModules, setEnabledModules] = useState([]);
  const [moduleFeatures, setModuleFeatures] = useState({}); // Add-on features per module
  // District roster-import flag. Starts off so the menu does not flash the
  // new item before the flag read returns. A failed read stays off.
  const [otaImportEnabled, setOtaImportEnabled] = useState(false);
  // District NFC tag flag. Starts off so the menu does not flash the item.
  const [otaNfcEnabled, setOtaNfcEnabled] = useState(false);
  // District-wide grant-work approver membership — separate from any
  // permission key. Being on the GrantApprover roster is itself the
  // authorization to see the Grant Approvals nav item / page, since
  // approvers are typically supervisors/payroll staff, not admins.
  const [isGrantApprover, setIsGrantApprover] = useState(false);
  // Tracks the tenant's `permissions_version` from `/api/rbac/me/`. We
  // poll the endpoint on a low-frequency interval and refresh local
  // state whenever the server-side version is higher than ours. This is
  // the cheap way to propagate platform-admin entitlement changes to
  // an already-loaded tenant session without forcing the user to log
  // out and back in.
  const [permissionsVersion, setPermissionsVersion] = useState(0);
  const [tenantId, setTenantId] = useState(null);
  const [tenantName, setTenantName] = useState('');
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [activeScope, setActiveScope] = useState(null); // For scope selector
  
  // Employee data for conditional menu items
  const [employeeData, setEmployeeData] = useState(null);
  
  // Platform admin district selector state
  const [availableTenants, setAvailableTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [selectedTenantName, setSelectedTenantName] = useState('');

  // Check if user is a platform admin (not district user)
  const isPlatformAdmin = user?.email?.endsWith('@k12erp.com') && user?.is_super_admin;
  
  // Check if user is a district admin (holds any admin.*.view permission)
  const isDistrictAdmin = userPermissions.some(p => p.startsWith('admin.') && p.endsWith('.view'));

  // True when the *real* logged-in user is one of the 4 SD308 named district-
  // admin accounts AND they're not in demo impersonation. Drives the stripped-
  // sidebar whitelist below — when impersonating, the impersonated role's
  // normal nav logic should apply instead.
  const isSd308AdminAccount = useMemo(() => {
    const email = (user?.email || '').toLowerCase();
    return SD308_ADMIN_EMAILS.includes(email);
  }, [user]);

  // Fetch available tenants for platform admins
  useEffect(() => {
    const fetchTenants = async () => {
      if (isPlatformAdmin) {
        try {
          const response = await api.get('/admin/tenants/');
          const tenants = response.data?.results || response.data || [];
          // Filter out the k12erp platform tenant
          const districtTenants = tenants.filter(t => t.schema_name !== 'k12erp' && t.schema_name !== 'public');
          setAvailableTenants(districtTenants);
          // Auto-select first tenant if none selected
          if (districtTenants.length > 0 && !selectedTenantId) {
            setSelectedTenantId(districtTenants[0].id);
            setSelectedTenantName(districtTenants[0].name);
          }
        } catch (error) {
          console.error('Failed to fetch tenants:', error);
        }
      }
    };
    fetchTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatformAdmin]);

  // Fetch user permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await api.get('/rbac/me/');
        setUserPermissions(response.data?.permissions || []);
        setUserRoles(response.data?.roles || []);
        setUserScope(response.data?.scope || null);
        setEnabledModules(response.data?.enabled_modules || []);
        setModuleFeatures(response.data?.module_features || {});
        setPermissionsVersion(response.data?.permissions_version || 0);
        setTenantId(response.data?.tenant_id || null);
        setTenantName(response.data?.tenant_name || '');
        setIsGrantApprover(!!response.data?.is_grant_approver);
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
        // If fetch fails and user is super admin, grant all modules that
        // are not behind a district flag. Flag-gated items stay hidden.
        if (user?.is_super_admin) {
          setUserPermissions(['*']);
          setUserScope({ type: 'district', names: [], description: 'Full access' });
          setEnabledModules(moduleKeysFromNav(navigationAfterPermissionFetchFailure()));
        }
      } finally {
        setPermissionsLoaded(true);
      }
    };

    if (user) {
      fetchPermissions();
    }
  }, [user]);


  // Live invalidation: poll /rbac/me/ every 60s and quietly refresh local
  // permission/module state. Propagates platform-admin entitlement toggles
  // to already-active sessions without forcing a logout.
  useEffect(() => {
    if (!user || !permissionsLoaded) return undefined;
    const POLL_MS = 60_000;
    const id = setInterval(async () => {
      try {
        const r = await api.get('/rbac/me/');
        setUserPermissions(r.data?.permissions || []);
        setUserRoles(r.data?.roles || []);
        setEnabledModules(r.data?.enabled_modules || []);
        setModuleFeatures(r.data?.module_features || {});
        setPermissionsVersion(r.data?.permissions_version || 0);
        setIsGrantApprover(!!r.data?.is_grant_approver);
      } catch {
        // Network blip — try again next tick.
      }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [user, permissionsLoaded]);

  // Listen for module entitlement changes and refresh modules/features.
  useEffect(() => {
    const handleModulesChanged = async () => {
      try {
        const response = await api.get('/rbac/me/');
        setEnabledModules(response.data?.enabled_modules || []);
        setModuleFeatures(response.data?.module_features || {});
      } catch (error) {
        console.error('Failed to refresh modules:', error);
      }
    };
    window.addEventListener('modulesChanged', handleModulesChanged);
    return () => window.removeEventListener('modulesChanged', handleModulesChanged);
  }, []);

  // Listen for role assignment/revocation changes and do a full permissions
  // refresh immediately — the 60s poll gates on permissions_version which is
  // a tenant-level field and never increments on per-user role changes, so
  // without this event the sidebar can stay stale for up to 60 seconds after
  // the admin makes a change (including changes to their own account).
  useEffect(() => {
    const handleRolesChanged = async () => {
      try {
        const r = await api.get('/rbac/me/');
        setUserPermissions(r.data?.permissions || []);
        setUserRoles(r.data?.roles || []);
        setEnabledModules(r.data?.enabled_modules || []);
        setModuleFeatures(r.data?.module_features || {});
        setPermissionsVersion(r.data?.permissions_version || 0);
        setIsGrantApprover(!!r.data?.is_grant_approver);
      } catch {}
    };
    window.addEventListener('rbac-roles-changed', handleRolesChanged);
    return () => window.removeEventListener('rbac-roles-changed', handleRolesChanged);
  }, []);

  // Fetch employee data for current user (to check pay_type)
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const response = await api.get('/hr/employees/me/');
        if (response.data?.id) {
          setEmployeeData(response.data);
        }
      } catch (error) {
        // Employee record might not exist for admins
        console.log('No employee record found for current user');
      }
    };

    if (user && !isPlatformAdmin) {
      fetchEmployeeData();
    }
  }, [user, isPlatformAdmin]);

  useEffect(() => {
    if (!user) {
      setOtaImportEnabled(false);
      setOtaNfcEnabled(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/feature-flags/client/');
        const keys = response.data?.keys;
        if (!cancelled) {
          const list = Array.isArray(keys) ? keys : [];
          setOtaImportEnabled(list.includes(ROSTER_IMPORT_FLAG));
          setOtaNfcEnabled(list.includes(NFC_TAP_FLAG));
        }
      } catch {
        if (!cancelled) {
          setOtaImportEnabled(false);
          setOtaNfcEnabled(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const hasModuleAccess = (module) => {
    return hasNavigationModuleAccess(module, {
      enabledModules,
      isSuperAdmin: !!user?.is_super_admin,
      isGrantApprover,
    });
  };

  // Phase 2 — RBAC Access Matrix sub-nav gate.
  // Returns true if the current user may see the given route's nav child.
  // Backward-compatible & fail-open: admins bypass; routes not in the matrix
  // are always shown; a module is only gated for a user once that user's
  // roles actually grant ≥1 matrix permission in that module.
  // Nav gating: is_super_admin bypasses; all other users pass only if
  // they hold the required permission key. No hardcoded role names here.
  // Modules where the user holds at least one .view grant — used to determine
  // whether a route is gated (has matrix config) vs ungated (always visible).
  const userMatrixModules = useMemo(() => {
    const mods = new Set();
    (userPermissions || []).forEach((k) => {
      if (typeof k === 'string' && k.endsWith('.view')) mods.add(k.split('.')[0]);
    });
    return mods;
  }, [userPermissions]);

  const canAccessRoute = (href, permKey) => {
    if (!href) return true;
    if (user?.is_super_admin) return true;
    // If the nav item carries a specific permission key, check it directly so
    // submodule-level toggles (e.g. Background Checks vs ATS) drive visibility.
    if (permKey) {
      return (userPermissions || []).includes(permKey);
    }
    // Module-level fallback for items without a specific permission key.
    const mod = href.split('/').filter(Boolean)[0];
    if (!mod || !userMatrixModules.has(mod)) return true;
    return (userPermissions || []).some((k) => k.startsWith(`${mod}.`) && k.endsWith('.view'));
  };

  const isSupervisorRole = useMemo(() => {
    return SUPERVISOR_ROLES.some(role => userRoles.includes(role));
  }, [userRoles]);
  
  // Check if user is a student
  const isStudent = useMemo(() => {
    return STUDENT_ROLES.some(role => userRoles.includes(role));
  }, [userRoles]);
  
  // Check if user is a parent
  const isParent = useMemo(() => {
    return PARENT_ROLES.some(role => userRoles.includes(role));
  }, [userRoles]);

  // Get scope badge color
  const getScopeBadgeColor = (scopeType) => {
    switch (scopeType) {
      case 'district': return 'bg-emerald-100 text-emerald-700';
      case 'department': return 'bg-blue-100 text-blue-700';
      case 'building': return 'bg-amber-100 text-amber-700';
      case 'team': return 'bg-purple-100 text-purple-700';
      case 'self': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Filter navigation based on user permissions AND enabled modules
  const filteredNavigation = useMemo(() => {
    // Wait for permissions to load before filtering
    if (!permissionsLoaded) {
      // Only show dashboard while loading
      return [{ name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, alwaysShow: true }];
    }

    // Students get student portal navigation (check before permissions check)
    if (isStudent) {
      return withPlannedSection(studentPortalNavigation);
    }
    
    // Parents get parent portal navigation (check before permissions check)
    if (isParent) {
      return withPlannedSection(parentPortalNavigation);
    }

    // For non-student/parent users, wait for permissions
    if (userPermissions.length === 0 && userRoles.length === 0 && !isGrantApprover) {
      return [{ name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, alwaysShow: true }];
    }

    const rosterImport = rosterImportNavVisible({
      flagOn: otaImportEnabled,
      permissions: userPermissions,
      isSuperAdmin: !!user?.is_super_admin,
    });
    const nfcTags = nfcTagNavVisible({
      flagOn: otaNfcEnabled,
      permissions: userPermissions,
      isSuperAdmin: !!user?.is_super_admin,
    });
    const baseNavigation = getBaseNavigation(isSupervisorRole, userRoles, false, userPermissions, { rosterImport, nfcTags });

    const visible = baseNavigation.filter(item => {
      // SD308 named-admin whitelist — strips everything not on the demo's
      // approved top-level list. Applied AFTER the role/module checks below
      // would otherwise pass, so it's a hard cap on what these 4 accounts see.
      if (isSd308AdminAccount && !SD308_ADMIN_NAV_WHITELIST.has(item.name)) return false;

      // Role-based hide check applies to all items, including alwaysShow.
      // We DO NOT bypass this in demo impersonation. Sub-item bypass (so D→A
      // inside Hiring & Time & Attendance can ADD items) is handled inside
      // getBaseNavigation via the isDemoImpersonating flag.
      if (item.hiddenForRoles?.some(r => userRoles.includes(r))) return false;

      // Always show items marked as alwaysShow
      if (item.alwaysShow) return true;

      // Support for multiple modules (show if ANY module is enabled)
      if (item.modules && Array.isArray(item.modules)) {
        return item.modules.some(m => hasModuleAccess(m));
      }

      // Items without module restriction - should not exist in main nav
      if (!item.module) return false;

      // Check module permission AND if module is enabled for tenant
      return hasModuleAccess(item.module);
    });
    return withPlannedSection(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPermissions, enabledModules, isPlatformAdmin, user, permissionsLoaded, isSupervisorRole, isStudent, isParent, isSd308AdminAccount, isGrantApprover, otaImportEnabled, otaNfcEnabled]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Flat list of all nav pages the user can access (all levels)
  const allNavPages = useMemo(() => {
    const pages = [];
    const flatten = (items, parentName, icon) => {
      items.forEach(item => {
        if (item.href) {
          pages.push({ name: item.name, href: item.href, parent: parentName, icon: item.icon || icon });
        }
        if (item.children?.length) {
          flatten(item.children, item.name, item.icon || icon);
        }
      });
    };
    flatten(filteredNavigation, null, null);
    return pages;
  }, [filteredNavigation]);

  // Filter nav pages by search query
  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults(null); setSearchOpen(false); return; }
    const q = searchQuery.toLowerCase();
    const matches = allNavPages.filter(p =>
      p.name.toLowerCase().includes(q) || p.parent?.toLowerCase().includes(q)
    );
    setSearchResults(matches);
    setSearchOpen(true);
  }, [searchQuery, allNavPages]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearchNavigate = useCallback((href) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(href);
  }, [navigate]);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isActive = (href) => sidebarIsActive(location.pathname, href);

  // Check if any child of a parent nav item is currently active
  const isParentActive = (item) => sidebarIsParentActive(location.pathname, item);

  // Auto-expand parent menu when a child route is active on navigation
  useEffect(() => {
    setExpandedMenu(findGroupToExpand(location.pathname, filteredNavigation));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, filteredNavigation]);

  // Get user role display
  const getUserRoleDisplay = () => {
    if (isPlatformAdmin) return 'Platform Super Admin';
    if (user?.is_super_admin) return 'Super Admin';
    if (userRoles.length > 0) return userRoles[0].replace('_', ' ');
    return 'User';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed inset-y-0 left-0 z-50 bg-slate-900 flex flex-col transition-all duration-300",
          collapsed ? "w-16" : "w-64",
          "hidden lg:flex"
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          {!collapsed && (
            <Link 
              to="/" 
              state={{ from: location.pathname }}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20">
                <span className="text-white font-bold">K</span>
              </div>
              <span className="text-white font-bold font-heading">K12 ERP</span>
            </Link>
          )}
          {collapsed && (
            <Link 
              to="/" 
              state={{ from: location.pathname }}
              className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center mx-auto shadow-lg shadow-blue-600/20 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <span className="text-white font-bold">K</span>
            </Link>
          )}
          <button 
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 text-slate-400 hover:text-white transition-colors hidden lg:block"
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          <div className="space-y-1">
            {filteredNavigation.map((item) => {
              if (item.isSection) {
                return (
                  <div
                    key={item.name}
                    className={cn('mt-4 mb-1', collapsed ? 'mx-2 border-t border-slate-800' : 'px-3 pt-2')}
                    data-testid={`nav-section-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {!collapsed && (
                      <span className="text-xs font-semibold uppercase tracking-wider text-amber-500/90">{item.name}</span>
                    )}
                  </div>
                );
              }
              // Hide the entire parent module if it has children AND every
              // child would be filtered out at render time (matrix All-D or
              // disabled feature flags). Without this, the collapsible header
              // still renders as an empty group — visually misleading because
              // it implies access the user doesn't actually have.
              const childFilter = (child) => {
                if (child.featureKey && !user?.is_super_admin) {
                  if (moduleFeatures[child.featureKey] === false) return false;
                }
                // Grant Approvals: membership on the GrantApprover roster is
                // itself the authorization, not a permission key — see
                // isGrantApprover fetched from /rbac/me/.
                if (child.requiresGrantApprover && !user?.is_super_admin && !isGrantApprover) return false;
                if (child.superAdminOnly && !user?.is_super_admin) return false;
                // Category headers with pre-filtered sub-children pass through —
                // their gating happened when the sub-children array was built.
                if (child.isCategory) return true;
                // permissionAny — child visible if the user holds ANY listed permission.
                // Used by hub-style items (e.g. Employee Timesheets tabs) that surface
                // multiple underlying views under one nav entry.
                if (child.permissionAny && !user?.is_super_admin) {
                  return child.permissionAny.some((p) => (userPermissions || []).includes(p));
                }
                if (!canAccessRoute(child.href, child.permission)) return false;
                return true;
              };
              if (item.children) {
                const visibleChildren = item.children.filter(childFilter);
                if (visibleChildren.length === 0) return null;
              }
              return (
              <div key={item.name}>
                {item.children ? (
                  <>
                    <button
                      onClick={() => setExpandedMenu(expandedMenu === item.name ? null : item.name)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                        isParentActive(item)
                          ? "bg-blue-600 text-white"
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      )}
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.icon && <item.icon size={20} />}
                      {!collapsed && <span>{item.name}</span>}
                      {item.planned && !collapsed && <PlannedMark />}
                    </button>

                    {/* Submenu */}
                    {expandedMenu === item.name && !collapsed && (
                      <div className="ml-9 mt-1 space-y-1 overflow-hidden">
                        {item.children.filter(childFilter).map((child) => (
                          child.isCategory ? (
                            // Render category with nested children — styled to match
                            // regular sublinks so the category header aligns with the
                            // other items in the submenu.
                            <div key={child.name}>
                              <button
                                onClick={() => toggleCategory(child.name)}
                                className="w-full flex items-center justify-between text-left px-3 py-2 rounded-md text-sm text-slate-400 hover:text-white hover:bg-slate-800/50 transition-colors"
                              >
                                <span className="flex items-center gap-2">
                                  {child.name}
                                  {child.badge && (
                                    <span className={cn("px-1.5 py-0.5 text-[10px] font-bold rounded", child.badgeColor || "bg-blue-500", "text-white")}>
                                      {child.badge}
                                    </span>
                                  )}
                                </span>
                                <ChevronRight size={14} className={cn("transition-transform shrink-0", expandedCategories.has(child.name) && "rotate-90")} />
                              </button>
                              {expandedCategories.has(child.name) && child.children && (
                                <div className="mt-1 space-y-0.5 border-l-2 border-slate-700 ml-2 pl-2">
                                  {child.children.map((subChild) => (
                                    <NavLink
                                      key={subChild.href}
                                      to={subChild.href}
                                      end
                                      className={({ isActive: active }) => cn(
                                        "flex items-center justify-between px-2 py-1.5 rounded-md text-sm transition-colors",
                                        active 
                                          ? "text-white bg-slate-800" 
                                          : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                                      )}
                                    >
                                      <span>{subChild.name}</span>
                                      {subChild.savings && (
                                        <span className="text-[10px] font-medium text-green-400 bg-green-900/30 px-1.5 py-0.5 rounded">
                                          {subChild.savings}
                                        </span>
                                      )}
                                    </NavLink>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            // Render regular child link
                            <NavLink
                              key={child.href}
                              to={child.href}
                              end
                              className={({ isActive: active }) => cn(
                                "block px-3 py-2 rounded-md text-sm transition-colors",
                                active 
                                  ? "text-white bg-slate-800" 
                                  : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                              )}
                            >
                              {child.name}
                            </NavLink>
                          )
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <NavLink
                    to={item.href}
                    className={({ isActive: active }) => cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                      active 
                        ? "bg-blue-600 text-white" 
                        : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    )}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.icon && <item.icon size={20} />}
                    {!collapsed && <span>{item.name}</span>}
                    {item.planned && !collapsed && <PlannedMark />}
                  </NavLink>
                )}
              </div>
              );
            })}
          </div>

          {/* Help & Docs Section — visible to any admin (district, IT, super, or platform).
              Intentionally NOT gated on tenantId so a district admin sees it even before
              tenant selection has loaded. Suppressed for the 4 SD308 named-admin demo
              accounts (Documentation Hub isn't on their whitelist). */}
          {!isSd308AdminAccount && ((isDistrictAdmin) || isSuperAdmin || isPlatformAdmin) && (
            <>
              <div className="my-4 border-t border-slate-800" />
              <div className={cn("px-3 py-2", collapsed && "hidden")}>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Help &amp; Docs
                </span>
              </div>
              <div className="space-y-1">
                <NavLink
                  to="/admin/docs"
                  className={({ isActive: active }) => cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                    active
                      ? "bg-indigo-600 text-white"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  )}
                  data-testid="nav-documentation-hub"
                >
                  <BookOpen size={20} />
                  {!collapsed && <span>Documentation Hub</span>}
                </NavLink>
              </div>
            </>
          )}


          {/* District Admin Section - For district admins and IT admins (NOT platform admins, NOT IT_DIRECTOR) */}
          {isDistrictAdmin && !isPlatformAdmin && tenantId && (
            <>
              <div className="my-4 border-t border-slate-800" />
              <div className={cn("px-3 py-2", collapsed && "hidden")}>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Admin
                </span>
              </div>
              <div className="space-y-1">
                {districtAdminNavigation.filter(item =>
                  // SD308 named-admin whitelist for the District Setup section.
                  (!isSd308AdminAccount || SD308_ADMIN_DISTRICT_SECTION_WHITELIST.has(item.name)) &&
                  // Honor per-item visibleForRoles (e.g. Role Hierarchy is
                  // TENANT_ADMIN-only) — merged from dev's filter chain.
                  (!item.visibleForRoles || item.visibleForRoles.some(r => userRoles.includes(r))) &&
                  // Registry submodule gate — hide exactly what <RequireSubmodule>
                  // blocks at the route, via the shared util (super-admin bypass).
                  canViewSubmodule(item.submodule, { permissions: userPermissions, isSuperAdmin, roles: userRoles })
                ).map((item) => {
                  // Handle dynamic href for onboarding wizard
                  const href = item.dynamic && item.href === '/district-onboarding' 
                    ? `/district-onboarding/${tenantId}` 
                    : item.href;
                  
                  return (
                    <NavLink
                      key={item.name}
                      to={href}
                      className={({ isActive: active }) => cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                        active 
                          ? "bg-green-600 text-white" 
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      )}
                      data-testid={`nav-district-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.icon && <item.icon size={20} />}
                      {!collapsed && <span>{item.name}</span>}
                      {item.planned && !collapsed && <PlannedMark />}
                    </NavLink>
                  );
                })}
              </div>
            </>
          )}

          {/* District Tools Section - For district admins only (Simulation + District Health).
              Suppressed for the 4 SD308 named-admin demo accounts. */}
          {isDistrictAdmin && !isPlatformAdmin && !isSd308AdminAccount && (
            <>
              <div className="my-4 border-t border-slate-800" />
              <div className={cn("px-3 py-2", collapsed && "hidden")}>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  District Tools
                </span>
              </div>
              <div className="space-y-1">
                {districtToolsNavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive: active }) => cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                      active 
                        ? "bg-purple-600 text-white" 
                        : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    )}
                    data-testid={`nav-tools-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.icon && <item.icon size={20} />}
                    {!collapsed && <span>{item.name}</span>}
                  </NavLink>
                ))}
              </div>
            </>
          )}

          {/* Platform Admin Section - Only for k12erp super admins */}
          {(isSuperAdmin || isPlatformAdmin) && (
            <>
              <div className="my-4 border-t border-slate-800" />
              <div className={cn("px-3 py-2", collapsed && "hidden")}>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Platform Admin
                </span>
              </div>
              <div className="space-y-1">
                {adminNavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive: active }) => cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                      active 
                        ? "bg-slate-700 text-white" 
                        : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    )}
                    data-testid={`nav-admin-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.icon && <item.icon size={20} />}
                    {!collapsed && <span>{item.name}</span>}
                  </NavLink>
                ))}
              </div>
            </>
          )}

          {/* Platform Tools - Diagnostics & Config (Platform Admin Only) */}
          {(isSuperAdmin || isPlatformAdmin) && (
            <>
              <div className="my-4 border-t border-slate-800" />
              <div className={cn("px-3 py-2", collapsed && "hidden")}>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Platform Tools
                </span>
              </div>
              <div className="space-y-1">
                {platformToolsNavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive: active }) => cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                      active 
                        ? "bg-indigo-600 text-white" 
                        : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    )}
                    data-testid={`nav-platform-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.icon && <item.icon size={20} />}
                    {!collapsed && <span>{item.name}</span>}
                  </NavLink>
                ))}
              </div>
            </>
          )}

          {/* District Management - Platform admin tools for managing specific districts */}
          {(isSuperAdmin || isPlatformAdmin) && (
            <>
              <div className="my-4 border-t border-slate-800" />
              <div className={cn("px-3 py-2", collapsed && "hidden")}>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  District Management
                </span>
              </div>
              
              {/* District Selector */}
              {!collapsed && availableTenants.length > 0 && (
                <div className="px-3 mb-2">
                  <select
                    value={selectedTenantId || ''}
                    onChange={(e) => {
                      const tenant = availableTenants.find(t => t.id === e.target.value);
                      setSelectedTenantId(e.target.value);
                      setSelectedTenantName(tenant?.name || '');
                    }}
                    className="w-full bg-slate-800 text-white text-sm rounded-lg px-3 py-2 border border-slate-700 focus:border-blue-500 focus:outline-none"
                    data-testid="district-selector"
                  >
                    {availableTenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {selectedTenantName && !collapsed && (
                <div className="px-3 mb-2 text-xs text-slate-400">
                  Managing: <span className="text-blue-400">{selectedTenantName}</span>
                </div>
              )}
              
              <div className="space-y-1">
                {districtManagementNavigation.map((item) => {
                  // Handle dynamic href for onboarding wizard with selected tenant
                  const href = item.dynamic && item.href === '/district-onboarding' && selectedTenantId
                    ? `/district-onboarding/${selectedTenantId}` 
                    : item.href;
                  
                  return (
                    <NavLink
                      key={item.name}
                      to={href}
                      className={({ isActive: active }) => cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                        active 
                          ? "bg-teal-600 text-white" 
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      )}
                      data-testid={`nav-district-mgmt-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.icon && <item.icon size={20} />}
                      {!collapsed && <span>{item.name}</span>}
                      {item.planned && !collapsed && <PlannedMark />}
                    </NavLink>
                  );
                })}
              </div>
            </>
          )}
        </nav>

        {/* Scope Indicator / Selector */}
        {!collapsed && userScope && !isPlatformAdmin && (
          <div className="px-3 pb-3">
            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <Eye size={14} className="text-slate-400" />
                <span className="text-xs font-medium text-slate-400 uppercase">Data Access</span>
              </div>
              
              {/* If user has multiple scopes, show a selector */}
              {userScope.names?.length > 1 ? (
                <div>
                  <select data-testid="dashboardlayout-activescope-select"
                    value={activeScope || userScope.names[0]}
                    onChange={(e) => {
                      setActiveScope(e.target.value);
                      localStorage.setItem('activeScope', e.target.value);
                      // Refresh the page to apply new scope filter
                      window.location.reload();
                    }}
                    className="w-full text-xs bg-slate-700 text-white border border-slate-600 rounded px-2 py-1.5"
                  >
                    {userScope.names.map((name, idx) => (
                      <option key={idx} value={name}>{name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Viewing: {activeScope || userScope.names[0]}
                  </p>
                </div>
              ) : (
                <Badge className={cn("text-xs", getScopeBadgeColor(userScope.type))}>
                  <MapPin size={10} className="mr-1" />
                  {userScope.type === 'district' && 'All District'}
                  {userScope.type === 'building' && (userScope.names?.[0] || 'Building')}
                  {userScope.type === 'department' && (userScope.names?.[0] || 'Department')}
                  {userScope.type === 'team' && 'Your Team'}
                  {userScope.type === 'self' && 'Self Only'}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Settings Link — Settings live as a tab on the profile page */}
        <div className="p-2 border-t border-slate-800">
          <NavLink
            to="/hr/my-profile?tab=settings"
            className={({ isActive: active }) => cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
              active 
                ? "bg-slate-700 text-white" 
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            )}
            data-testid="nav-settings"
          >
            <Settings size={20} />
            {!collapsed && <span>Settings</span>}
          </NavLink>
        </div>
      </aside>

      {/* Main Content */}
      <div className={cn("flex-1 flex flex-col transition-all duration-300 min-w-0 overflow-x-hidden", collapsed ? "lg:ml-16" : "lg:ml-64")}>
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-40">
          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-3 min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-600 hover:text-slate-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Menu size={24} />
          </button>

          {/* Back navigation — hidden on /dashboard. Uses in-app history
              when available (location.key !== 'default'), falls back to
              /dashboard so a direct link still has somewhere safe to go. */}
          {location.pathname !== '/dashboard' && (
            <button
              type="button"
              onClick={() => {
                if (location.key && location.key !== 'default') {
                  navigate(-1);
                } else {
                  navigate('/dashboard');
                }
              }}
              className="mr-2 p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center"
              title="Back"
              aria-label="Go back"
              data-testid="topbar-back-btn"
            >
              <ChevronLeft size={20} />
            </button>
          )}

          {/* Search */}
          <div className="hidden md:flex items-center flex-1 max-w-xl" ref={searchRef}>
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => searchResults && setSearchOpen(true)}
                placeholder="Search modules and sub-modules..."
                className="w-full pl-10 pr-4 py-2 bg-slate-100 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="global-search"
              />
              {searchOpen && searchResults && (
                <div className="absolute top-full mt-1 w-full bg-white rounded-lg shadow-lg border border-slate-200 z-50 max-h-72 overflow-y-auto">
                  {searchResults.length > 0 ? searchResults.map((page, idx) => (
                    <button key={idx} className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-3"
                      onClick={() => handleSearchNavigate(page.href)}>
                      {page.icon && <page.icon size={15} className="text-slate-400 shrink-0" />}
                      <div>
                        <span className="font-medium text-slate-800">{page.name}</span>
                        {page.parent && <span className="ml-2 text-xs text-slate-400">{page.parent}</span>}
                      </div>
                    </button>
                  )) : (
                    <div className="px-4 py-6 text-center text-sm text-slate-400">No modules found for "{searchQuery}"</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
            {/* Quick Clock-In shortcut — shown when user has timesheet access */}
            {(userPermissions || []).includes('attendance.my_timesheet.view') && (
              <Link
                to="/attendance/mobile-clock"
                className="relative p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Clock in / out"
                data-testid="quick-clockin-btn"
                aria-label="Quick clock-in"
              >
                <Clock size={20} />
              </Link>
            )}

            {/* Notifications */}
            <NotificationBell />

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-3 hover:bg-slate-100 rounded-lg p-1.5 transition-colors"
                  data-testid="user-menu-btn"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.avatar_url} />
                    <AvatarFallback className="bg-blue-600 text-white text-sm">
                      {getInitials(user?.first_name + ' ' + user?.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left">
                    <div className="text-sm font-medium text-slate-900">
                      {user?.first_name} {user?.last_name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {getUserRoleDisplay()}
                    </div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/hr/my-profile?tab=settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="bg-purple-600 text-white px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye size={18} />
              <span className="font-medium">
                Impersonating: {user?.first_name} {user?.last_name} ({user?.email})
              </span>
              {(() => {
                const impData = sessionStorage.getItem('impersonation_data');
                if (impData) {
                  const parsed = JSON.parse(impData);
                  if (parsed.impersonated_user?.tenant) {
                    return (
                      <span className="text-purple-200 ml-2">
                        — Tenant: {parsed.impersonated_user.tenant} | Type: {parsed.impersonated_user.type}
                      </span>
                    );
                  }
                }
                return (
                  <span className="text-purple-200">
                    — Viewing as this user for debugging
                  </span>
                );
              })()}
            </div>
            <Button data-testid="dashboardlayout-exit-impersonation-btn"
              size="sm"
              variant="outline"
              className="bg-white hover:bg-purple-100 border-purple-300 text-purple-900"
              onClick={async () => {
                try {
                  const { returnTo } = await stopImpersonation();
                  window.location.href = returnTo || '/admin/rbac#matrix';
                } catch (err) {
                  console.error('Failed to stop impersonation:', err);
                  toast.error(err?.response?.data?.error || 'Failed to exit impersonation. Please try again.');
                }
              }}
            >
              Exit Impersonation
            </Button>
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <UpcomingDueBanner items={dueBannerItems} />
          <Outlet />
        </main>
      </div>

      <OvertimeRequestBanner />

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 flex flex-col lg:hidden overflow-y-auto">
            {/* Logo */}
            <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
              <Link 
                to="/" 
                state={{ from: location.pathname }}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20">
                  <span className="text-white font-bold">K</span>
                </div>
                <span className="text-white font-bold font-heading">K12 ERP</span>
              </Link>
              <button onClick={() => setMobileMenuOpen(false)} className="p-1 text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            {/* Nav */}
            <nav className="flex-1 py-4 overflow-y-auto">
              {filteredNavigation.map((section) => {
                if (section.isSection) {
                  return (
                    <div key={section.name} className="mt-4 mb-1 px-6" data-testid={`nav-section-${section.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      <span className="text-xs font-semibold uppercase tracking-wider text-amber-500/90">{section.name}</span>
                    </div>
                  );
                }
                // Match the desktop sidebar: apply featureKey + canAccessRoute
                // gating to children, and skip the entire parent collapsible
                // if no children survive. Without this, mobile users see the
                // parent module + every child regardless of matrix toggles.
                const childFilter = (child) => {
                  if (child.featureKey && !user?.is_super_admin) {
                    if (moduleFeatures[child.featureKey] === false) return false;
                  }
                  if (child.requiresGrantApprover && !user?.is_super_admin && !isGrantApprover) return false;
                if (child.superAdminOnly && !user?.is_super_admin) return false;
                  if (child.isCategory) return true;
                  if (child.permissionAny && !user?.is_super_admin) {
                    return child.permissionAny.some((p) => (userPermissions || []).includes(p));
                  }
                  if (!canAccessRoute(child.href, child.permission)) return false;
                  return true;
                };
                let visibleChildren = null;
                if (section.children) {
                  visibleChildren = section.children.filter(childFilter);
                  if (visibleChildren.length === 0) return null;
                }
                return (
                <div key={section.name} className="px-3 py-2">
                  <button
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                      location.pathname.startsWith(`/${section.name.toLowerCase().replace(/\s+&?\s*/g, '-')}`)
                        ? "bg-white/10 text-white"
                        : "text-slate-400 hover:text-white hover:bg-white/5"
                    )}
                    onClick={() => {
                      if (section.children?.length) {
                        setExpandedMenu(expandedMenu === section.name ? null : section.name);
                      } else if (section.href) {
                        navigate(section.href);
                        setMobileMenuOpen(false);
                      }
                    }}
                  >
                    {section.icon && <section.icon size={20} />}
                    <span>{section.name}</span>
                    {section.planned && <PlannedMark />}
                  </button>
                  {expandedMenu === section.name && visibleChildren && (
                    <div className="ml-4 mt-1 space-y-0.5">
                      {visibleChildren.map(child => (
                        child.isCategory ? (
                          <div key={child.name} className="mt-2">
                            <div className="px-3 py-1 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                              {child.name}
                            </div>
                            {(child.children || []).map((subChild) => (
                              <button
                                key={subChild.href}
                                onClick={() => { navigate(subChild.href); setMobileMenuOpen(false); }}
                                className={cn(
                                  "w-full text-left flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
                                  location.pathname === subChild.href
                                    ? "bg-blue-600/20 text-blue-400"
                                    : "text-slate-400 hover:text-white hover:bg-white/5"
                                )}
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />
                                {subChild.name}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <button
                            key={child.href}
                            onClick={() => { navigate(child.href); setMobileMenuOpen(false); }}
                            className={cn(
                              "w-full text-left flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
                              location.pathname === child.href
                                ? "bg-blue-600/20 text-blue-400"
                                : "text-slate-400 hover:text-white hover:bg-white/5"
                            )}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />
                            {child.name}
                          </button>
                        )
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
            </nav>
          </aside>
        </>
      )}
    </div>
  );
};

export default DashboardLayout;
